import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  FormLabel,
  InputGroup,
  InputMessage,
  InputText,
  Stack,
  Text,
} from "@aksara-ui/react";
import ImageDropzone from "components/ImageDropzone";
import { Field, FieldProps, Form, Formik, FormikValues } from "formik";
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { storeReward, updateReward } from "redux/actions";
import styled from "styled-components";
import * as yup from "yup";
import moment from "moment";
import { InputSelect } from "components/Form/InputSelect";
import { getRole } from "hooks";

const FormWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const GridWrapper = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

interface Props {
  voucherId: string;
}

interface ValueSchema {
  label: string;
  value: string | number;
}

const initialValues = {
  voucherName: "",
  pointsNeeded: "",
  expiryDate: "",
  voucherType: "",
  merchant: "",
  pointTypeUsed: "",
  isAcitive: "1",
  maxClaim: "0",
};

const VoucherFormModal: React.FC<Props & DialogProps> = ({
  isOpen,
  onClose,
  voucherId,
}) => {
  const isAddMode = !voucherId;
  const dispatch = useDispatch();
  const userRole = getRole();
  const formikRef = useRef<FormikValues>() as any;

  const [merchant, setMerchant] = useState<ValueSchema | null>(null);
  const [voucherType, setVoucherType] = useState<ValueSchema | null>(null);
  const [pointTypeUsed, setPointTypeUsed] = useState<ValueSchema | null>(null);
  const [tenant, setTenant] = useState<ValueSchema | null>(null);
  const [filesVoucherImage, setFilesVoucherImage] = useState<any>([]);
  const [filesVoucherImageDetail, setFilesVoucherImageDetail] = useState<any>([]);
  const { appUrl } = useSelector((state: any) => state.globalReducer);
  const { vouchers, isFormLoading } = useSelector(
    (state: any) => state.voucherReducer
  );

  const [merchantItems] = React.useState([
    { label: "CGV", value: "CGV" },
    { label: "Indomaret", value: "Indomaret" },
    { label: "Alfamart", value: "Alfamart" },
    { label: "Tokopedia", value: "Tokopedia" },
    { label: "Ticket Concert", value: "Ticket Concert" },
    { label: "Pulsa Elektronik", value: "Pulsa Elektronik" },
    { label: "E-Wallet", value: "E-Wallet" },
    { label: "Warehouse", value: "Warehouse" },
    { label: "Voucher", value: "Voucher" },
    { label: "Tiket Rekreasi", value: "Tiket Rekreasi" },
  ]);
  const [voucherTypeItems] = React.useState([
    { label: "Auto", value: "auto" },
    { label: "Manual", value: "manual" },
  ]);
  const [pointTypeUsedItems] = React.useState([
    { label: "XP Point", value: 0 },
    { label: "Reward Point", value: 1 },
    { label: "Push Reward", value: 2 },

  ]);
  const [tenantItems] = React.useState([
   { label: "Ancol Points", value: 3 },
  ]);

  const handleCloseModal = () => {
    onClose && onClose();
    setMerchant(null);
    setVoucherType(null);
    setPointTypeUsed(null);
    setTenant(null);
    setFilesVoucherImage([]);
  };

  const handleSubmit = (values: any) => {
    const {
      voucherName,
      pointsNeeded,
      expiryDate,
      voucherType,
      merchant,
      pointTypeUsed,
      tenantId,
      isActive,
      maxClaim,  // Add this line
    } = values;
    const voucherImage =
      typeof filesVoucherImage[0] === "object" ? filesVoucherImage[0] : null;

    const voucherImageDetail =
      typeof filesVoucherImageDetail[0] === "object" ? filesVoucherImageDetail[0] : null;

    isAddMode
      ? dispatch(
          storeReward(
            {
              voucherName,
              pointsNeeded,
              expiryDate,
              voucherType,
              merchant,
              pointTypeUsed,
              voucherImage,
              voucherImageDetail,
              tenantId,
              isActive,
              maxClaim,  // Add this line
            },
            handleCloseModal
          ) as any
        )
      : dispatch(
          updateReward(
            voucherId,
            {
              voucherName,
              pointsNeeded,
              expiryDate,
              voucherType,
              merchant,
              pointTypeUsed,
              voucherImage,
              voucherImageDetail,
              tenantId,
              isActive,
              maxClaim,  // Add this line
            },
            handleCloseModal
          ) as any
        );
  };

  useEffect(() => {
    if (!isAddMode) {
      const voucherDetail = vouchers.find(
        (voucher: any) => voucher.id === parseInt(voucherId)
      );

      if (formikRef.current) {
        formikRef.current.setFieldValue(
          "voucherName",
          voucherDetail.catalog_name
        );
        formikRef.current.setFieldValue(
          "pointsNeeded",
          voucherDetail.points_needed
        );
        formikRef.current.setFieldValue(
          "expiryDate",
          moment(voucherDetail.expiry_date).format("YYYY-MM-DD")
        );
        formikRef.current.setFieldValue(
          "voucherType",
          voucherDetail.catalog_type
        );
        formikRef.current.setFieldValue(
          "merchant",
          voucherDetail.catalog_product
        );
        formikRef.current.setFieldValue(
          "pointTypeUsed",
          voucherDetail.point_type_used
        );
        formikRef.current.setFieldValue("tenantId", voucherDetail.tenant_id);
        formikRef.current.setFieldValue("isActive", voucherDetail.is_active);
        formikRef.current.setFieldValue(
          "maxClaim",
          voucherDetail.max_claim
        );

        merchantItems.map((item) =>
          item.value === voucherDetail.catalog_product
            ? setMerchant(item)
            : null
        );
        voucherTypeItems.map((item) =>
          item.value === voucherDetail.catalog_type
            ? setVoucherType(item)
            : null
        );
        pointTypeUsedItems.map((item) =>
          item.value.toString() === voucherDetail.point_type_used
            ? setPointTypeUsed(item)
            : null
        );
        tenantItems.map((item) =>
          item.value.toString() === voucherDetail.tenant_id
            ? setTenant(item)
            : null
        );

        setFilesVoucherImage([`${appUrl}/storage/${voucherDetail.img_url}`]);
        setFilesVoucherImageDetail([`${appUrl}/storage/${voucherDetail.img_detail_url}`]);
      }
    }
  }, [isAddMode, vouchers, voucherId, appUrl]);

  const validationSchema = yup.object({
    voucherName: yup.string().required("Voucher Name is required field"),
    pointsNeeded: yup.number().required("Points Needed is required field"),
    expiryDate: yup.date().required("Expiry Date is required field"),
    voucherType: yup.string().required("Voucher Type is required field"),
    merchant: yup.string().required("Merchant is required field"),
    pointTypeUsed: yup.number().required("Point Type Used is required field"),
    tenantId:
      userRole === "superadmin"
        ? yup.string().required("Tenant is required field")
        : yup.string(),
    isActive:
      userRole === "superadmin"
        ? yup.string().required("Status is required field")
        : yup.string(),
    maxClaim: yup
      .number()
      .min(0, "Max Claim must be 0 or greater")
      .required("Max Claim is required field"),
  });

  return (
    <Dialog
      disableOverlayClick
      isOpen={isOpen}
      onClose={() => handleCloseModal()}
      overflowY={"auto"}
    >
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue }) => {
          return (
            <Form>
              <DialogHeader>
                <Text fontWeight={700}>
                  {isAddMode ? "Add Voucher" : "Update Voucher"}
                </Text>
              </DialogHeader>
              <DialogContent>
                <FormWrapper>
                  <Field name="voucherName">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="voucherName">
                          Voucher Name
                        </FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            type="text"
                            size={40}
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <GridWrapper>
                    <Field name="pointsNeeded">
                      {({ field, meta }: FieldProps<string>) => (
                        <Stack spacing="xs">
                          <FormLabel htmlFor="pointsNeeded">
                            Points Needed
                          </FormLabel>
                          <InputGroup inputSize={40}>
                            <InputText
                              type="number"
                              size={40}
                              errors={!!meta.error && meta.touched}
                              {...field}
                            />
                          </InputGroup>
                          {!!meta.error && meta.touched && (
                            <InputMessage variant="error">
                              {meta.error}
                            </InputMessage>
                          )}
                        </Stack>
                      )}
                    </Field>
                    <Field name="expiryDate">
                      {({ field, meta }: FieldProps<string>) => (
                        <Stack spacing="xs">
                          <FormLabel htmlFor="expiryDate">
                            Expiry Date
                          </FormLabel>
                          <InputGroup inputSize={40}>
                            <InputText
                              type="date"
                              size={40}
                              errors={!!meta.error && meta.touched}
                              {...field}
                            />
                          </InputGroup>
                          {!!meta.error && meta.touched && (
                            <InputMessage variant="error">
                              {meta.error}
                            </InputMessage>
                          )}
                        </Stack>
                      )}
                    </Field>
                  </GridWrapper>
                  <Field name="merchant">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="merchant">Merchant</FormLabel>
                        <InputGroup inputSize={40}>
                          <InputSelect
                            selectedItem={merchant}
                            width={"100%"}
                            placeholder="Select Merchant"
                            handleSelectedItemChange={({ selectedItem }) => {
                              if (selectedItem) {
                                setMerchant(selectedItem);
                                setFieldValue("merchant", selectedItem.value);
                              }
                            }}
                            itemToString={(item) =>
                              item ? `${item.label}` : ""
                            }
                            itemValue={(item) => (item ? `${item.value}` : "")}
                            items={merchantItems}
                            size={"lg"}
                            borderRadius={"6px"}
                            errors={!!meta.error && meta.touched}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <GridWrapper>
                    <Field name="voucherType">
                      {({ field, meta }: FieldProps<string>) => (
                        <Stack spacing="xs">
                          <FormLabel htmlFor="voucherType">
                            Voucher Type
                          </FormLabel>
                          <InputGroup inputSize={40}>
                            <InputSelect
                              selectedItem={voucherType}
                              width={"100%"}
                              placeholder="Select Voucher Type"
                              handleSelectedItemChange={({ selectedItem }) => {
                                if (selectedItem) {
                                  setVoucherType(selectedItem);
                                  setFieldValue(
                                    "voucherType",
                                    selectedItem.value
                                  );
                                }
                              }}
                              itemToString={(item) =>
                                item ? `${item.label}` : ""
                              }
                              itemValue={(item) =>
                                item ? `${item.value}` : ""
                              }
                              items={voucherTypeItems}
                              size={"lg"}
                              borderRadius={"6px"}
                              errors={!!meta.error && meta.touched}
                            />
                          </InputGroup>
                          {!!meta.error && meta.touched && (
                            <InputMessage variant="error">
                              {meta.error}
                            </InputMessage>
                          )}
                        </Stack>
                      )}
                    </Field>
                    <Field name="pointTypeUsed">
                      {({ field, meta }: FieldProps<string>) => (
                        <Stack spacing="xs">
                          <FormLabel htmlFor="pointTypeUsed">
                            Point Type Used
                          </FormLabel>
                          <InputGroup inputSize={40}>
                            <InputSelect
                              selectedItem={pointTypeUsed}
                              width={"100%"}
                              placeholder="Select Point Type Used"
                              handleSelectedItemChange={({ selectedItem }) => {
                                if (selectedItem) {
                                  setPointTypeUsed(selectedItem);
                                  setFieldValue(
                                    "pointTypeUsed",
                                    selectedItem.value
                                  );
                                }
                              }}
                              itemToString={(item) =>
                                item ? `${item.label}` : ""
                              }
                              itemValue={(item) =>
                                item ? `${item.value}` : ""
                              }
                              items={pointTypeUsedItems}
                              size={"lg"}
                              borderRadius={"6px"}
                              errors={!!meta.error && meta.touched}
                            />
                          </InputGroup>
                          {!!meta.error && meta.touched && (
                            <InputMessage variant="error">
                              {meta.error}
                            </InputMessage>
                          )}
                        </Stack>
                      )}
                    </Field>
                  </GridWrapper>
                  {userRole === "superadmin" ? (
                    <Field name="tenantId">
                      {({ field, meta }: FieldProps<string>) => (
                        <Stack spacing="xs">
                          <FormLabel htmlFor="tenantId">Tenant</FormLabel>
                          <InputGroup inputSize={40}>
                            <InputSelect
                              selectedItem={tenant}
                              width={"100%"}
                              placeholder="Select Tenant"
                              handleSelectedItemChange={({ selectedItem }) => {
                                if (selectedItem) {
                                  setTenant(selectedItem);
                                  setFieldValue("tenantId", selectedItem.value);
                                }
                              }}
                              itemToString={(item) =>
                                item ? `${item.label}` : ""
                              }
                              itemValue={(item) =>
                                item ? `${item.value}` : ""
                              }
                              items={tenantItems}
                              size={"lg"}
                              borderRadius={"6px"}
                              errors={!!meta.error && meta.touched}
                            />
                          </InputGroup>
                          {!!meta.error && meta.touched && (
                            <InputMessage variant="error">
                              {meta.error}
                            </InputMessage>
                          )}
                        </Stack>
                      )}
                    </Field>
                  ) : (
                    <></>
                  )}
                  <Field name="isActive">
                  {({ field, meta }: FieldProps<string>) => (
                    <Stack spacing="xs">
                      <FormLabel htmlFor="isActive">Status</FormLabel>
                      <InputGroup inputSize={40}>
                        <InputSelect
                          selectedItem={
                            field.value
                              ? { label: field.value === "1" ? "Active" : "Inactive", value: field.value }
                              : null
                          }
                          width={"100%"}
                          placeholder="Pilih Status"
                          handleSelectedItemChange={({ selectedItem }) => {
                            if (selectedItem) {
                              setFieldValue("isActive", selectedItem.value);
                            }
                          }}
                          itemToString={(item) => (item ? `${item.label}` : "")}
                          itemValue={(item) => (item ? `${item.value}` : "")}
                          items={[
                            { label: "Active", value: "1" },
                            { label: "Inactive", value: "0" }
                          ]}
                          size={"lg"}
                          borderRadius={"6px"}
                          errors={!!meta.error && meta.touched}
                        />
                      </InputGroup>
                      {!!meta.error && meta.touched && (
                        <InputMessage variant="error">
                          {meta.error}
                        </InputMessage>
                      )}
                    </Stack>
                  )}
                </Field>    
                  <Field name="maxClaim">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="maxClaim">Max Claim</FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            type="number"
                            size={40}
                            defaultValue="0"
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <Stack spacing="xs">
                    <FormLabel>Image</FormLabel>
                    <ImageDropzone
                      name="voucher_image"
                      files={filesVoucherImage}
                      setFiles={setFilesVoucherImage}
                    />
                  </Stack>
                  <Stack spacing="xs">
                    <FormLabel>Image Detail</FormLabel>
                    <ImageDropzone
                       name="voucher_image"
                      files={filesVoucherImageDetail}
                      setFiles={setFilesVoucherImageDetail}
                    />
                  </Stack>
                </FormWrapper>
              </DialogContent>
              <DialogFooter>
                <Stack
                  display="flex"
                  flexDirection="row"
                  spacing="xs"
                  flex={1}
                  style={{ gap: 15 }}
                >
                  <Button
                    block
                    variant="outline"
                    onClick={() => handleCloseModal()}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    isLoading={isFormLoading}
                    block
                    variant="primary"
                  >
                    {isAddMode ? "Add" : "Update"}
                  </Button>
                </Stack>
              </DialogFooter>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default VoucherFormModal;
